import React, {useEffect, useState} from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Select
} from '@mui/material';
import {Add, Delete, Edit, Save} from '@mui/icons-material';
import SoftBox from '../../../components/SoftBox';
import SoftButton from '../../../components/SoftButton';
import SoftTypography from '../../../components/SoftTypography';
import childApiClient from '../../../api/children/childrenApiClient';
import {getAllGuardians} from '../../../api/guardian/guardianApiClient';
import PropTypes from 'prop-types';

const GuardianRelationshipManager = ({open, onClose, childId, guardianRelationships = [], onUpdate}) => {
    const [guardians, setGuardians] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [editingRelationship, setEditingRelationship] = useState(null);
    const [addGuardianDialog, setAddGuardianDialog] = useState(false);
    const [selectedGuardianId, setSelectedGuardianId] = useState('');
    const [selectedRelationship, setSelectedRelationship] = useState('GUARDIAN');
    const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
    const [guardianToDelete, setGuardianToDelete] = useState(null);

    const relationships = [
        {value: 'MOTHER', label: 'Mother'},
        {value: 'FATHER', label: 'Father'},
        {value: 'GUARDIAN', label: 'Guardian'},
        {value: 'OTHER', label: 'Other'}
    ];

    useEffect(() => {
        if (open) {
            console.log('GuardianRelationshipManager opened with:', {
                childId,
                guardianRelationships: guardianRelationships.length,
                relationships: guardianRelationships
            });
            loadGuardians();
        }
    }, [open, childId, guardianRelationships]);

    const loadGuardians = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getAllGuardians();
            console.log('Loaded guardians:', response.data?.length || 0, 'guardians');
            setGuardians(response.data || []);
        } catch (error) {
            console.error('Error loading guardians:', error);
            setError('Failed to load guardians');
            setGuardians([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (relationship) => {
        setGuardianToDelete(relationship);
        setDeleteConfirmDialog(true);
    };

    const handleConfirmDelete = async () => {
        if (!guardianToDelete) return;
        
        try {
            setLoading(true);
            setError(null);
            await childApiClient.removeGuardianFromChild(childId, guardianToDelete.guardianId);

            // Reset dialog state first
            setDeleteConfirmDialog(false);
            setGuardianToDelete(null);

            // Use requestAnimationFrame to ensure DOM updates are complete
            if (onUpdate) {
                requestAnimationFrame(() => {
                    onUpdate();
                });
            }
        } catch (error) {
            console.error('Error removing guardian:', error);
            setError('Failed to remove guardian');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelDelete = () => {
        setDeleteConfirmDialog(false);
        setGuardianToDelete(null);
    };

    const handleUpdateRelationship = async (guardianId, relationship) => {
        try {
            setLoading(true);
            await childApiClient.updateGuardianRelationship(childId, guardianId, {relationship});

            // Reset edit state first
            setEditMode(false);
            setEditingRelationship(null);

            // Use requestAnimationFrame to ensure DOM updates are complete
            if (onUpdate) {
                requestAnimationFrame(() => {
                    onUpdate();
                });
            }
        } catch (error) {
            console.error('Error updating relationship:', error);
            setError('Failed to update relationship');
        } finally {
            setLoading(false);
        }
    };

    const handleAddGuardian = async () => {
        if (!selectedGuardianId) {
            setError('Please select a guardian');
            return;
        }

        try {
            setLoading(true);
            await childApiClient.addGuardianToChild(childId, {
                guardianId: parseInt(selectedGuardianId),
                relationship: selectedRelationship
            });

            // Reset form state first
            setAddGuardianDialog(false);
            setSelectedGuardianId('');
            setSelectedRelationship('GUARDIAN');

            // Use React's flushSync to ensure state updates are processed before calling onUpdate
            if (onUpdate) {
                // Use requestAnimationFrame to ensure DOM updates are complete
                requestAnimationFrame(() => {
                    onUpdate();
                });
            }
        } catch (error) {
            console.error('Error adding guardian:', error);
            setError('Failed to add guardian');
        } finally {
            setLoading(false);
        }
    };

    const getGuardianName = (relationship) => {
        // First try to get from the relationship data itself
        if (relationship.guardianFirstName && relationship.guardianLastName) {
            return `${relationship.guardianFirstName} ${relationship.guardianLastName}`;
        }

        // Fallback to looking up in guardians array by ID
        const guardian = guardians.find(g => g.id === relationship.guardianId);
        return guardian ? `${guardian.firstName} ${guardian.lastName}` : 'Unknown Guardian';
    };

    const getRelationshipLabel = (relationship) => {
        const rel = relationships.find(r => r.value === relationship);
        return rel ? rel.label : relationship;
    };

    const availableGuardians = guardians.filter(guardian => {
        const isAlreadyAssigned = guardianRelationships.some(rel =>
            rel.guardianId === guardian.id ||
            rel.guardianId === parseInt(guardian.id) // Handle string/number ID mismatches
        );
        return !isAlreadyAssigned;
    });

    // Debug logging
    console.log('Available guardians filter:', {
        totalGuardians: guardians.length,
        assignedRelationships: guardianRelationships.length,
        availableGuardians: availableGuardians.length,
        assignedGuardianIds: guardianRelationships.map(rel => rel.guardianId)
    });

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    <SoftTypography variant="h6">Manage Guardian Relationships</SoftTypography>
                </DialogTitle>
                <DialogContent>
                    {error && (
                        <SoftBox mb={2}>
                            <SoftTypography color="error">{error}</SoftTypography>
                        </SoftBox>
                    )}

                    <SoftBox mb={2}>
                        <SoftButton
                            variant="contained"
                            color="primary"
                            startIcon={<Add/>}
                            onClick={() => setAddGuardianDialog(true)}
                            disabled={loading || availableGuardians.length === 0}
                        >
                            Add Guardian
                        </SoftButton>
                        {availableGuardians.length === 0 && guardians.length > 0 && (
                            <SoftTypography variant="caption" color="text.secondary" sx={{ml: 2}}>
                                All available guardians are already assigned
                            </SoftTypography>
                        )}
                        {guardians.length === 0 && !loading && (
                            <SoftTypography variant="caption" color="text.secondary" sx={{ml: 2}}>
                                No guardians available in the system
                            </SoftTypography>
                        )}
                    </SoftBox>

                    {loading && guardianRelationships.length === 0 ? (
                        <SoftBox textAlign="center" py={3}>
                            <SoftTypography variant="body2" color="textSecondary">
                                Loading guardian information...
                            </SoftTypography>
                        </SoftBox>
                    ) : (
                        <List>
                            {guardianRelationships.map((relationship) => (
                            <ListItem key={relationship.guardianId} divider>
                                <ListItemText
                                    primary={getGuardianName(relationship)}
                                    secondary={relationship.guardianEmail || 'Email not available'}
                                />
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                    {editMode && editingRelationship?.guardianId === relationship.guardianId ? (
                                        <FormControl size="small" sx={{minWidth: 120}}>
                                            <Select
                                                value={editingRelationship.relationship}
                                                onChange={(e) => setEditingRelationship({
                                                    ...editingRelationship,
                                                    relationship: e.target.value
                                                })}
                                            >
                                                {relationships.map((rel) => (
                                                    <MenuItem key={rel.value} value={rel.value}>
                                                        {rel.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <Chip
                                            label={getRelationshipLabel(relationship.relationship)}
                                            color="primary"
                                            variant="outlined"
                                        />
                                    )}

                                    <IconButton
                                        onClick={() => {
                                            if (editMode && editingRelationship?.guardianId === relationship.guardianId) {
                                                handleUpdateRelationship(relationship.guardianId, editingRelationship.relationship);
                                            } else {
                                                setEditMode(true);
                                                setEditingRelationship(relationship);
                                            }
                                        }}
                                        disabled={loading}
                                    >
                                        {editMode && editingRelationship?.guardianId === relationship.guardianId ?
                                            <Save/> : <Edit/>}
                                    </IconButton>

                                    <IconButton
                                        onClick={() => handleDeleteClick(relationship)}
                                        disabled={loading}
                                        color="error"
                                    >
                                        <Delete/>
                                    </IconButton>
                                </Box>
                            </ListItem>
                            ))}
                        </List>
                    )}

                    {!loading && guardianRelationships.length === 0 && (
                        <SoftBox textAlign="center" py={3}>
                            <SoftTypography variant="body2" color="textSecondary">
                                No guardians assigned to this child
                            </SoftTypography>
                        </SoftBox>
                    )}
                </DialogContent>
                <DialogActions>
                    <SoftButton onClick={onClose} disabled={loading}>
                        Close
                    </SoftButton>
                </DialogActions>
            </Dialog>

            {/* Add Guardian Dialog */}
            <Dialog open={addGuardianDialog} onClose={() => setAddGuardianDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Guardian to Child</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{mt: 1}}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel shrink>Select Guardian</InputLabel>
                                <Select
                                    value={selectedGuardianId}
                                    onChange={(e) => setSelectedGuardianId(e.target.value)}
                                    label="Select Guardian"
                                    displayEmpty
                                >
                                    <MenuItem value="" disabled>
                                        <em>Please select a guardian</em>
                                    </MenuItem>
                                    {availableGuardians.map((guardian) => (
                                        <MenuItem key={guardian.id} value={guardian.id}>
                                            {guardian.firstName} {guardian.lastName} ({guardian.email})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel >Relationship</InputLabel>
                                <Select
                                    value={selectedRelationship}
                                    onChange={(e) => setSelectedRelationship(e.target.value)}
                                    label="Relationship"
                                    displayEmpty
                                >
                                    <MenuItem value="" disabled>
                                        <em>Please select a relationship</em>
                                    </MenuItem>
                                    {relationships.map((rel) => (
                                        <MenuItem key={rel.value} value={rel.value}>
                                            {rel.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddGuardianDialog(false)} disabled={loading}>
                        Cancel
                    </Button>
                    <Button onClick={handleAddGuardian} variant="contained" disabled={loading}>
                        Add Guardian
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmDialog}
                onClose={handleCancelDelete}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <SoftTypography variant="h6" color="error">
                        Remove Guardian Relationship
                    </SoftTypography>
                </DialogTitle>
                <DialogContent>
                    <SoftTypography variant="body1">
                        Are you sure you want to remove{' '}
                        <strong>{guardianToDelete ? getGuardianName(guardianToDelete) : ''}</strong>{' '}
                        from this child&apos;s guardian relationships?
                    </SoftTypography>
                    <SoftBox mt={2}>
                        <SoftTypography variant="body2" color="textSecondary">
                            This action will permanently remove the guardian relationship.
                            The guardian will no longer have access to this child&apos;s information.
                        </SoftTypography>
                    </SoftBox>
                </DialogContent>
                <DialogActions>
                    <SoftButton
                        onClick={handleCancelDelete}
                        disabled={loading}
                        color="text"
                    >
                        Cancel
                    </SoftButton>
                    <SoftButton
                        onClick={handleConfirmDelete}
                        disabled={loading}
                        color="error"
                        variant="contained"
                    >
                        {loading ? 'Removing...' : 'Remove Guardian'}
                    </SoftButton>
                </DialogActions>
            </Dialog>
        </>
    );
};

GuardianRelationshipManager.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    childId: PropTypes.number.isRequired,
    guardianRelationships: PropTypes.arrayOf(
        PropTypes.shape({
            guardianId: PropTypes.number.isRequired,
            guardianFirstName: PropTypes.string.isRequired,
            guardianLastName: PropTypes.string.isRequired,
            guardianEmail: PropTypes.string.isRequired,
            relationship: PropTypes.string.isRequired
        })
    ),
    onUpdate: PropTypes.func
};

export default GuardianRelationshipManager;
